import { UiState } from './ui-state';
import { FacetFilter, FacetValue, FilterType, isRangeFilter } from './types';
import { ParamParser } from '@app/core/search/routing/param-parser';
import * as RANGE from '@app/core/search/common/range';

export class UiStateBuilder {
  private uiState: UiState = {};
  private paramParser: ParamParser;

  constructor() {
    this.paramParser = new ParamParser();
  }

  static getStateProp(filterType: FilterType): string {
    const enumName = FilterType[filterType];
    return enumName.charAt(0).toLowerCase() + enumName.slice(1);
  }

  static getFacetValue(facet: FacetFilter, uiState: UiState): FacetValue {
    const stateProp = UiStateBuilder.getStateProp(facet.type);

    if (stateProp in uiState && facet.attributeName in uiState[stateProp]) {
      return uiState[stateProp][facet.attributeName];
    }

    return null;
  }

  setQuery(query: string): UiStateBuilder {
    this.uiState.query = query;
    return this;
  }

  setPage(page: number): UiStateBuilder {
    if (page < 0) {
      throw new Error('Page number must be a positive integer');
    }
    this.uiState.page = page;
    return this;
  }

  setPageParam(page: string): UiStateBuilder {
    const pageNumber = parseInt(page, 10);

    if (pageNumber > 0) {
      this.setPage(pageNumber);
    }

    return this;
  }

  setHitsPerPage(nbHits: number): UiStateBuilder {
    this.uiState.hitsPerPage = nbHits;
    return this;
  }

  sortBy(indexName: string): UiStateBuilder {
    this.uiState.sortBy = indexName;
    return this;
  }

  setFacetParam(facet: FacetFilter, param: string, value: string): UiStateBuilder {
    if (isRangeFilter(facet.type)) {
      const facetValue = UiStateBuilder.getFacetValue(facet, this.uiState);
      const nextRange: RANGE.Range = {};
      let currentRange: RANGE.Range = {};

      if (typeof facetValue === 'string') {
        currentRange = RANGE.parse(facetValue);
      }
      if (param.endsWith('Min')) {
        nextRange.min = parseFloat(value);
      }
      if (param.endsWith('Max')) {
        nextRange.max = parseFloat(value);
      }
      return this.setFacet(facet, RANGE.stringify(RANGE.merge(currentRange, nextRange)));
    }

    return this.setFacet(facet, this.paramParser.parse(facet.type, value));
  }

  setFacet(facet: FacetFilter, value: FacetValue): UiStateBuilder {
    const stateProp = UiStateBuilder.getStateProp(facet.type);

    if (!(stateProp in this.uiState)) {
      this.uiState[stateProp] = {};
    }

    this.uiState[stateProp][facet.attributeName] = value;

    return this;
  }

  setMenu(attributeName: string, value: string): UiStateBuilder {
    return this.setFacet({ type: FilterType.Menu, attributeName }, value);
  }

  setHierarchicalMenu(attributeName: string, value: string): UiStateBuilder {
    return this.setFacet({ type: FilterType.HierarchicalMenu, attributeName }, value);
  }

  setRefinementList(attributeName: string, value: string[]): UiStateBuilder {
    return this.setFacet({ type: FilterType.RefinementList, attributeName }, value);
  }

  setNumericRefinementList(attributeName: string, value: number): UiStateBuilder {
    return this.setFacet({ type: FilterType.NumericRefinementList, attributeName }, value);
  }

  setNumericSelector(attributeName: string, value: number): UiStateBuilder {
    return this.setFacet({ type: FilterType.NumericSelector, attributeName }, value);
  }

  setRange(attributeName: string, value: string): UiStateBuilder {
    return this.setFacet({ type: FilterType.Range, attributeName }, value);
  }

  setPriceRanges(attributeName: string, value: string): UiStateBuilder {
    return this.setFacet({ type: FilterType.PriceRanges, attributeName }, value);
  }

  setStarRating(attributeName: string, value: number): UiStateBuilder {
    return this.setFacet({ type: FilterType.StarRating, attributeName }, value);
  }

  setToggle(attributeName: string, value: boolean): UiStateBuilder {
    return this.setFacet({ type: FilterType.Toggle, attributeName }, value);
  }

  getState(): UiState {
    return this.uiState;
  }
}
